import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { RecordType } from '@/components/shared/DeleteRecordModal'
import { getCSRFToken, formatErrorMessage } from '@/utils/requests'
import { Record } from '@/types'
import { createAction } from '@reduxjs/toolkit'
import { apiPortals, apiProjects, apiAttachments } from '@/routes'

function getController(recordType: RecordType) {
  switch (recordType) {
    case RecordType.Portal:
      return apiPortals
    case RecordType.Project:
      return apiProjects
    case RecordType.Attachment:
      return apiAttachments
  }
}

interface DeleteRecordParams {
  recordIdOrSlug: string | number
  recordType: RecordType,
  record?: Record,
}

export const deleteRecordSuccess = createAction<DeleteRecordParams>('api/deleteRecord')

const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({
    baseUrl: '/',
    prepareHeaders(headers, { getState, type }) {
      if (type === 'mutation') {
        headers.set('X-CSRF-Token', getCSRFToken())
      }
      headers.set('Accept', 'application/json')
      return headers
    },
    // Typelizer generates routes with lowercase HTTP methods (e.g., 'patch', 'post').
    // Puma's HTTP parser requires uppercase methods. This normalizes them before sending.
    async fetchFn(input, init) {
      if (input instanceof Request && input.method) {
        // Can't modify Request method after creation, need to recreate
        const newRequest = new Request(input, {
          method: input.method.toUpperCase(),
        })
        return fetch(newRequest)
      }
      const response = await fetch(input, init)

      if (!response.ok) {
        const rawBody = await response.text()
        const data = rawBody ? JSON.parse(rawBody) : null
        const error = new Error(formatErrorMessage(data, response)) as Error & { status: number }
        error.status = response.status
        throw error
      }

      return response
    },
  }),
  endpoints: (builder) => ({
    deleteRecord: builder.mutation<Record, DeleteRecordParams>({
      query: ({ recordType, recordIdOrSlug }) => getController(recordType).destroy(recordIdOrSlug),
      async onQueryStarted({ recordType, recordIdOrSlug }, lifecycleThunk) {
        const { data: record } = await lifecycleThunk.queryFulfilled

        lifecycleThunk.dispatch(deleteRecordSuccess({ recordType, recordIdOrSlug, record }))
      },
    }),
  }),
})

export const { useDeleteRecordMutation } = apiSlice

export default apiSlice
